import styled, { css } from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import { borderRadius } from "../../../design-system/tokens/borders";
import { controlSize } from "../../../design-system/tokens/controlSizes";
import { duration, easing } from "../../../design-system/tokens/transitions";
import { shadows } from "../../../design-system/tokens/shadows";
import { homePageTokens } from "../../../design-system/tokens/home";
import {
  fontWeights,
  typographyMixin,
} from "../../../design-system/tokens/typography";
import {
  borderRadius as uiBorderRadius,
  fontSizes,
  spacing as uiSpacing,
} from "@magicbi/ui-library";
import type { SegmentedPickerVariant } from "./SegmentedPicker.types";

/**
 * Figma 3728:484453 "Modes".
 * Shell: white, stroke[100] (#EBF2FE), radius 8, pad 2.
 */
export const Shell = styled.div<{
  $variant: SegmentedPickerVariant;
  /**
   * `form` normally fills its row edge-to-edge, which is right for the
   * text-labelled delivery-condition picker it was built for. An icon-only
   * `form` picker (Chart Studio's legend position) has nothing to fill that
   * width with, so the shell instead shrinks to its four glyphs — Figma
   * 6409:556344 hugs the icons rather than stretching the border across the
   * panel.
   */
  $iconOnly?: boolean;
  $small?: boolean;
  $fullWidth?: boolean;
  $onDark?: boolean;
  $thumb?: boolean;
}>`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  background: ${rawColors.structural.white};
  border-radius: ${borderRadius.lg};
  box-sizing: border-box;

  ${({ $variant, $iconOnly }) =>
    $variant === "form"
      ? css`
          width: ${$iconOnly ? "auto" : "100%"};
          height: ${controlSize.field.md};
          padding: ${spacing[1]};
          border: none;
          box-shadow: inset 0 0 0 1px ${rawColors.stroke[200]};
          /* No overflow clip here (unlike the other variants): this is the
             only variant an icon-only option's hover Tooltip renders in, and
             the bubble sits outside the option's own box (Tooltip.styles.ts
             PLACEMENT) — a clipped Shell would hide it before it ever shows. */
        `
      : $variant === "dataStudio"
        ? css`
            width: 100%;
            height: ${({ theme }) => theme.spacing.base[10]};
            padding: ${({ theme }) => theme.spacing.base[1]};
            border: ${({ theme }) => theme.borders.width[1]}
              ${({ theme }) => theme.borders.style.solid}
              ${({ theme }) => theme.colors.border.stroke};
            overflow: hidden;
          `
      : $variant === "tabs"
        ? css`
            gap: ${spacing[0.5]};
            background: transparent;
            border: none;
            border-radius: 0;
          `
      : $variant === "track"
        ? css`
            gap: ${spacing[0.5]};
            padding: 0.1875rem; /* GAP: 3px track padding */
            background: ${({ theme }) => theme.colors.background.tertiary};
            border: none;
            border-radius: ${uiBorderRadius[10]};
          `
      : $variant === "inline"
        ? css`
            padding: ${spacing[0.5]};
            background: ${({ theme }) => theme.colors.background.secondary};
            border: 1px solid ${({ theme }) => theme.colors.border.stroke};
            overflow: hidden;
          `
      : $variant === "homeModes"
        ? css`
            gap: ${spacing[0.5]};
            padding: ${spacing[0.5]} ${spacing[0.5]};
            border: none;
            border-radius: ${borderRadius.xl};
            background: ${rawColors.grey[300]};
            overflow: hidden;
          `
      : $variant === "pill"
        ? css`
            gap: ${spacing[0.5]};
            /* GAP: design pad 3px; nearest spacing token is 4px. */
            padding: ${spacing[1]};
            border: none;
            border-radius: ${borderRadius.full};
            background: ${rawColors.grey[300]};
            overflow: hidden;
          `
      : $variant === "underline"
        ? css`
            width: 100%;
            height: ${spacing[10]};
            align-items: flex-end;
            gap: ${spacing[0.5]};
            padding: 0 ${spacing[2.5]};
            border-bottom: 1px solid ${rawColors.stroke[200]};
            border-radius: 0;
            background: transparent;
          `
      : $variant === "chartStudio"
        ? css`
            width: 100%;
            flex: 0 0 ${spacing[9]};
            height: ${spacing[9]};
            padding: ${spacing[1]} ${spacing[0.5]};
            /*
             * Figma 6395:549612 is 36 tall with a 28 option inside 4 padding —
             * the stroke sits on the frame edge and consumes no layout. A real
             * 1px border would leave only 26px of content box and clip the
             * active pill top and bottom; an inset ring keeps the full 28.
             */
            border: none;
            box-shadow:
              inset 0 0 0 1px ${({ theme }) => theme.colors.border.stroke},
              ${homePageTokens.shadow.modeSelector};
            overflow: hidden;
          `
        : css`
          padding: ${spacing[0.5]};
          border: 1px solid ${rawColors.stroke[100]};
        `}
  ${({ $variant, $small }) =>
    $variant === "homeModes" && $small
      ? css`
          height: 30px; /* GAP: no spacing token for 30px */
          padding: 3px; /* GAP: design pad 3px */
        `
      : ""}
  /* tone="onDark": a visible track on a dark bar. */
  ${({ $variant, $onDark }) =>
    $variant === "inline" && $onDark
      ? css`
          background: ${rawColors.grey[700]};
          border-color: ${rawColors.grey[500]};
        `
      : ""}
  /* Opt-in: the track fills its row and the options share it equally. */
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      display: flex;
      width: 100%;
    `}
`;

/**
 * Figma 3728:484454 / 484457 "_SegmentedPicker-option".
 * Pad 2×10 — 10px has no spacing token (GAP: between spacing[2]=8 and [3]=12).
 * Active radius 7 — between borderRadius.md (6) and lg (8); same as ViewModeToggle.
 */
export const OptionButton = styled.button<{
  $active: boolean;
  $variant: SegmentedPickerVariant;
  $optionId?: string;
  /** See `Shell`'s `$iconOnly` — the same icon-only `form` case. */
  $iconOnly?: boolean;
  $small?: boolean;
  $fullWidth?: boolean;
  $onDark?: boolean;
  /** The picker draws a sliding thumb, so the active option keeps no fill of its own. */
  $thumb?: boolean;
}>`
  appearance: none;
  box-sizing: border-box;
  margin: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: ${({ $active }) =>
    $active ? rawColors.primary[100] : "transparent"};
  cursor: pointer;
  white-space: nowrap;

  ${({ $variant, $active, $optionId, $iconOnly }) =>
    $variant === "form"
      ? css`
          ${$iconOnly
            ? css`
                flex: 0 0 auto;
              `
            : css`
                flex: 1 1 0;
                min-width: 0;
              `}
          height: 100%;
          padding: ${spacing[1]} ${$iconOnly ? spacing[3] : spacing[2]};
          border-radius: ${borderRadius.sm};
          ${typographyMixin("caption", 3)}
          color: ${$active
            ? rawColors.text.highEmphasis
            : rawColors.text.lowEmphasis};
          box-shadow: ${$active ? shadows.elevation[1] : shadows.none};
        `
      : $variant === "dataStudio"
        ? css`
            flex: 1 1 0;
            min-width: 0;
            height: 100%;
            gap: ${({ theme }) => theme.spacing.base[2]};
            padding: ${({ theme }) => theme.spacing.base[1]}
              ${({ theme }) => theme.spacing.base[2]};
            border-radius: calc(
              ${({ theme }) => theme.borders.radius.lg} -
                ${({ theme }) => theme.borders.width[1]}
            );
            ${$active
              ? typographyMixin("body", 3)
              : typographyMixin("body", 4)}
            color: ${({ theme }) =>
              $active
                ? theme.colors.primary[500]
                : theme.colors.text.secondary};
          `
      : $variant === "tabs"
        ? css`
            flex: none;
            height: 1.875rem; /* GAP: 30px tab height */
            padding: 0 ${uiSpacing[3]};
            border-radius: ${borderRadius.md};
            font-size: 0.75rem; /* GAP: 12px tab text, no size token */
            font-weight: ${fontWeights.semibold};
            color: ${({ theme }) =>
              $active ? theme.colors.primary[500] : theme.colors.text.secondary};
          `
      : $variant === "track"
        ? css`
            flex: none;
            height: 1.875rem; /* GAP: 30px option height */
            padding: 0 ${uiSpacing[3.5]};
            border-radius: ${borderRadius[7]};
            font-size: ${fontSizes["13"]};
            font-weight: ${fontWeights.semibold};
            background: ${({ theme }) =>
              $active ? theme.colors.surface.primary : "transparent"};
            color: ${({ theme }) =>
              $active ? theme.colors.text.primary : theme.colors.text.secondary};
            box-shadow: ${$active ? shadows.elevation[2] : shadows.none};
          `
      : $variant === "inline"
        ? css`
            flex: 0 0 auto;
            padding: ${spacing[0.5]} ${spacing[2]};
            border-radius: ${borderRadius.md};
            ${$active
              ? typographyMixin("caption", 3)
              : typographyMixin("caption", 4)}
            background: ${({ theme }) =>
              $active ? theme.colors.background.primary : "transparent"};
            color: ${({ theme }) =>
              $active ? theme.colors.text.primary : theme.colors.text.secondary};
          `
      : $variant === "homeModes"
        ? css`
            height: ${spacing[7]};
            gap: ${spacing[1.5]};
            padding: 0 ${spacing[3]};
            border-radius: ${borderRadius.lg};
            ${$active
              ? typographyMixin("caption", 1)
              : typographyMixin("caption", 2)}
            background: ${$active ? rawColors.structural.white : "transparent"};
            box-shadow: ${$active ? shadows.elevation[1] : shadows.none};
            color: ${$active
              ? rawColors.text.highEmphasis
              : rawColors.text.mediumEmphasis};
          `
      : $variant === "pill"
        ? css`
            height: ${spacing[7]};
            gap: ${spacing[1.5]};
            /* GAP: design pad 14px; nearest spacing token is 12px. */
            padding: 0 ${spacing[3]};
            border-radius: ${borderRadius.full};
            ${typographyMixin("caption", 1)}
            background: ${$active ? rawColors.structural.white : "transparent"};
            box-shadow: ${$active ? shadows.elevation[1] : shadows.none};
            color: ${$active
              ? rawColors.text.highEmphasis
              : rawColors.text.mediumEmphasis};

            /* Not-yet-built options read as muted text, not a faded pill. */
            &:disabled {
              opacity: 1;
              color: ${rawColors.text.subtleEmphasis};
            }
          `
      : $variant === "underline"
        ? css`
            flex: 0 0 auto;
            height: calc(${spacing[10]} - 2px);
            padding: 0 ${spacing[2.5]};
            border-radius: 0;
            background: transparent;
            border-bottom: 2px solid
              ${$active ? rawColors.text.interactive : "transparent"};
            ${typographyMixin("caption", $active ? 1 : 2)}
            color: ${$active
              ? rawColors.text.interactive
              : rawColors.text.mediumEmphasis};
          `
      : $variant === "chartStudio"
        ? css`
            /* Keep every segment equal-width as selection changes. */
            flex: 1 1 0;
            min-width: 0;
            height: ${spacing[7]};
            gap: 0.375rem;
            /* Figma active "Configure" pill (node 6395:549612) is 10px padding on
               all sides; inactive icon-only tabs keep the tighter 2px/8px.
               Configure and AI insights get +3px on request; Format is unchanged. */
            padding: ${(() => {
              const extra =
                $optionId === "configure" || $optionId === "ai-insights"
                  ? "3px"
                  : "0px";
              return $active
                ? `calc(${spacing[2.5]} + ${extra})`
                : `calc(${spacing[0.5]} + ${extra}) calc(${spacing[2]} + ${extra})`;
            })()};
            border-radius: 0.4375rem;
            /* $active comes from the enclosing interpolation; destructuring it
               again here is untyped -- ExecutionContext carries only theme. */
            background: ${({ theme }) =>
              $active ? theme.colors.primary[100] : "transparent"};
            ${$active
              ? typographyMixin("body", 3)
              : typographyMixin("body", 4)}
            color: ${({ theme }) =>
              $active
                ? theme.colors.text.interactive
                : theme.colors.text.secondary};
          `
        : css`
          padding: ${spacing[0.5]} 10px;
          border-radius: 7px;
          /* Figma Overline 3 = 10/14 Medium; token overline[3] is 14px — same gap as chart chips. */
          ${typographyMixin("overline", 4)}
          font-weight: ${fontWeights.medium};
          color: ${$active
            ? rawColors.primary[500]
            : rawColors.text.mediumEmphasis};
          line-height: 14px;
        `}

  /* tone="onDark": the active option is a white pill with dark text, the rest light text. */
  ${({ $variant, $onDark, $active }) =>
    $variant === "inline" && $onDark
      ? css`
          background: ${$active ? rawColors.structural.white : "transparent"};
          color: ${$active ? rawColors.text.highEmphasis : rawColors.grey[300]};
          box-shadow: ${$active ? shadows.elevation[1] : shadows.none};
          &:disabled:not([aria-checked="true"]) {
            color: ${rawColors.grey[500]};
          }
        `
      : ""}

  ${({ $thumb }) =>
    $thumb &&
    css`
      position: relative;
      z-index: 1;
      background: transparent;
      box-shadow: none;
    `}
  /* The active fill fades between options and a resting option tints on hover. */
  transition:
    background-color ${duration.fast} ${easing.smooth},
    color ${duration.fast} ${easing.smooth},
    box-shadow ${duration.fast} ${easing.smooth};
  &:hover:not(:disabled):not([aria-checked="true"]):not([aria-selected="true"]) {
    background-color: ${rawColors.primary[50]};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:focus-visible {
    outline: 2px solid ${rawColors.primary[500]};
    outline-offset: 1px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 1;
  }
  /* Disabled stays readable (F18): muted text, not a faded pill. */
  &:disabled:not([aria-checked="true"]) {
    color: ${rawColors.text.mediumEmphasis};
  }
  ${({ $variant, $small }) =>
    $variant === "homeModes" && $small
      ? css`
          height: 24px; /* GAP: no spacing token for 24px */
        `
      : ""}
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      flex: 1 1 0;
      min-width: 0;
    `}
`;

/** After the label: a count or a tag. */
export const OptionTrailing = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  margin-left: ${spacing[1.5]};
`;

export const OptionIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;

  svg {
    width: ${({ theme }) => theme.spacing.base[4]};
    height: ${({ theme }) => theme.spacing.base[4]};
  }

  img {
    width: ${({ theme }) => theme.spacing.base[4]};
    height: ${({ theme }) => theme.spacing.base[4]};
  }
`;

/** Wraps the SoonBadge after a label; the badge is a flex child so it needs its own gap. */
export const SoonMark = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  margin-left: ${spacing[1]};
`;

/**
 * Chart Studio preserves the label in the accessibility tree but collapses
 * unselected tabs to their 16px icon, matching Figma 6433:558405.
 */
export const OptionLabel = styled.span<{
  $active: boolean;
  $variant: SegmentedPickerVariant;
}>`
  ${({ $active, $variant }) =>
    $variant === "chartStudio" && !$active
      ? css`
          position: absolute;
          width: 1px;
          height: 1px;
          margin: -1px;
          overflow: hidden;
          clip: rect(0 0 0 0);
          white-space: nowrap;
        `
      : null}
`;

/**
 * Figma's `Divider` is a line of width 0 sitting on the option boundary, so it
 * consumes no layout. Keeping it 1px wide in flow made every option's width
 * depend on how many dividers the current selection happened to render, so the
 * tabs shifted as the user switched between them. The hairline is painted by a
 * pseudo-element instead and the span itself takes zero width.
 */
export const OptionDivider = styled.span`
  position: relative;
  flex: 0 0 0;
  width: 0;
  height: ${spacing[7]};
  align-self: center;

  &::after {
    content: "";
    position: absolute;
    inset: 0 auto 0 0;
    width: 1px;
    background: ${rawColors.stroke[200]};
  }
`;

/** The sliding fill behind the active option (opt-in `slidingThumb`). Sits under the option buttons. */
export const Thumb = styled.span<{ $variant: SegmentedPickerVariant; $ready: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  pointer-events: none;
  background: ${rawColors.structural.white};
  box-shadow: ${shadows.elevation[1]};
  border-radius: ${({ $variant }) => ($variant === "pill" ? borderRadius.full : borderRadius.lg)};
  transition: ${({ $ready }) =>
    $ready
      ? css`transform ${duration.normal} ${easing.smooth}, width ${duration.normal} ${easing.smooth}`
      : "none"};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
